import axios from "axios";

interface Labels {
  [key: string]: string;
}

export class Logger {
  private static instance: Logger;
  private lokiUrl: string;
  private labels: Labels;
  private logBuffer: {
    timestamp: string;
    message: string;
    level: string;
    metadata?: object;
  }[] = [];
  private batchSize: number;
  private batchInterval: number;
  private timer: any;

  private constructor(
    lokiUrl: string,
    labels: Labels,
    batchSize = 10,
    batchInterval = 5000
  ) {
    this.lokiUrl = lokiUrl;
    this.labels = { ...labels, server_name: window.location.hostname };
    this.batchSize = batchSize;
    this.batchInterval = batchInterval;
    this.startBatching();
  }

  public static getInstance(
    lokiUrl = process.env.REACT_APP_LOKI_URL || "http://109.123.248.67:3101",
    labels = {
      app: `intranet-frontend-${process.env.REACT_APP_ENVIRONMENT || "prod"}`,
    },
    batchSize = 10,
    batchInterval = 5000
  ): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger(lokiUrl, labels, batchSize, batchInterval);
    }
    return Logger.instance;
  }

  private startBatching() {
    this.timer = setInterval(() => {
      this.sendBufferedLogs();
    }, this.batchInterval);
  }

  private sendBufferedLogs() {
    if (this.logBuffer.length === 0) return;

    // Se o ambiente for local, envia para o console em vez do Loki
    if (process.env.REACT_APP_HOSTING_ENV === "local") {
      this.logBuffer.forEach((log) => {
        const { level, message, metadata } = log;
        // Mapeia o nível de log para a função de console correspondente
        const logFunction = (console as any)[level] || console.log;
        logFunction(`[${level.toUpperCase()}] - ${message}`, metadata);
      });
      this.logBuffer = []; // Limpa o buffer
      return; // Interrompe a função para não enviar ao Loki
    }

    const logEntries = this.logBuffer.map((log) => {
      // Combina mensagem e metadados num único JSON para facilitar o parsing no Grafana/Loki
      const logLine = JSON.stringify({
        level: log.level,
        message: log.message,
        metadata: log.metadata,
        timestamp: new Date(Number(log.timestamp) / 1000000).toISOString(),
      });

      return {
        stream: {
          ...this.labels,
          level: log.level,
        },
        values: [[log.timestamp, logLine]],
      };
    });

    const logEntry = {
      streams: logEntries,
    };

    // if (process.env.REACT_APP_ENVIRONMENT === "develop") return;

    axios
      .post(`${this.lokiUrl}/loki/api/v1/push`, logEntry, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then(() => {
        this.logBuffer = [];
      })
      .catch((error) => {
        if (axios.isAxiosError(error)) {
          console.error(
            "Erro ao enviar logs para o Loki (Axios):",
            error.message,
            error.response?.data,
            error.response?.status
          );
        } else {
          console.error("Erro ao enviar logs para o Loki (Outro):", error);
        }
      });
  }

  info(message: string, metadata?: object) {
    this.log("info", message, metadata);
  }

  error(message: string, metadata?: object) {
    this.log("error", message, metadata);
  }

  warn(message: string, metadata?: object) {
    this.log("warn", message, metadata);
  }

  debug(message: string, metadata?: object) {
    this.log("debug", message, metadata);
  }

  private log(level: string, message: string, metadata?: object) {
    // Captura o stack trace para encontrar o local da chamada.
    const stack = new Error().stack?.split("\n");
    let callSite = "unknown";
    if (stack && stack.length > 3) {
      // A linha relevante do stack trace geralmente é a 4ª:
      // stack[0] = "Error"
      // stack[1] = at Logger.log (...)
      // stack[2] = at Logger.error/info/warn/debug (...)
      // stack[3] = o local da chamada original
      const line = stack[3].trim();
      const match = line.match(/\((.*)\)|at (.*)/); // Tenta capturar o conteúdo entre parênteses ou depois de "at "
      if (match) {
        callSite = match[1] || match[2] || "unknown";
      }
    }

    const timestamp = (Date.now() * 1000000).toString(); // Timestamp como string
    const finalMetadata = { ...metadata, callSite };

    this.logBuffer.push({ timestamp, message, level, metadata: finalMetadata });
    if (this.logBuffer.length >= this.batchSize) {
      this.sendBufferedLogs();
    }
  }

  stop() {
    clearInterval(this.timer);
    this.sendBufferedLogs();
  }
}
